import { useCallback, useEffect, useRef, useState } from 'react'
import {
  EXPORT_ALL_QUEUED_FALLBACK,
  EXPORT_POLL_INTERVAL_MS,
  EXPORT_QUEUED_FALLBACK,
} from '../constants'
import { useUserRole } from '@/hooks/useUserRole'
import { showApiError, showInfo } from '@/lib/utils/notifications'
import {
  exportReimbursements,
  getLatestExportStatus,
} from '../services/reimbursementsService'
import type { ExportStatus, ReimbursementFilters } from '../types'

function isProcessingStatus(status: string | null | undefined): boolean {
  return String(status || '').toUpperCase() === 'PROCESSING'
}

export function useExportPolling() {
  const { user } = useUserRole()
  const userId = (user as { user_id?: number | string } | undefined)?.user_id
  const [status, setStatus] = useState<ExportStatus | null>(null)
  const [triggering, setTriggering] = useState(false)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const checkStatusRef = useRef<() => Promise<void>>(async () => {})

  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
  }, [])

  /** Start the interval only when not already running (Angular resumes while PROCESSING). */
  const schedulePolling = useCallback(() => {
    if (intervalRef.current) return
    intervalRef.current = setInterval(() => {
      void checkStatusRef.current()
    }, EXPORT_POLL_INTERVAL_MS)
  }, [])

  const checkStatus = useCallback(async () => {
    if (userId === undefined || userId === null) return
    try {
      const next = await getLatestExportStatus(userId)
      setStatus(next)
      if (isProcessingStatus(next.status)) {
        schedulePolling()
      } else {
        stopPolling()
      }
    } catch (e) {
      showApiError(e)
      stopPolling()
    }
  }, [userId, schedulePolling, stopPolling])

  checkStatusRef.current = checkStatus

  const startPolling = useCallback(() => {
    stopPolling()
    void checkStatus()
  }, [checkStatus, stopPolling])

  const triggerExport = useCallback(
    async (filters: ReimbursementFilters, scope: 'export' | 'exportAll') => {
      setTriggering(true)
      try {
        const result = await exportReimbursements(filters, scope)
        const raw = result?.message
        const msg =
          typeof raw === 'string' && raw.trim() !== ''
            ? raw.trim()
            : scope === 'exportAll'
              ? EXPORT_ALL_QUEUED_FALLBACK
              : EXPORT_QUEUED_FALLBACK
        showInfo(msg)
        startPolling()
      } catch (e) {
        showApiError(e)
      } finally {
        setTriggering(false)
      }
    },
    [startPolling],
  )

  // Mount / when userId is available — fetch latest status and resume polling if PROCESSING
  // (Angular `exportAllPolling()` on defaults list init).
  useEffect(() => {
    void checkStatus()
    return stopPolling
  }, [checkStatus, stopPolling])

  const dismissStatus = useCallback(() => {
    stopPolling()
    setStatus(null)
  }, [stopPolling])

  return {
    status,
    triggering,
    isProcessing: isProcessingStatus(status?.status),
    triggerExport,
    refreshStatus: checkStatus,
    dismissStatus,
  }
}
